import { Alert, Snackbar } from "@mui/material"
import { ChangeEvent, FormEvent, useEffect, useState } from "react"
import {
  Button,
  Col,
  Container,
  FormControl,
  InputGroup,
  OverlayTrigger,
  Row,
  Table,
  Tooltip,
} from "react-bootstrap"
import { TableProps } from "../../interfaces/CamposTablaGenerica"
import "./GenericTable.css"

function GenericTable<T>({
  data,
  columns,
  actions,
  onAdd,
  onImport,
  onExport,
  onUpdate,
  onDelete,
  onView,
  onCopy,
  onSendPublicQrWhatsapp,
  onRecibos,
  onRecibosEvento,
  onRecibosEventoItem,
  showRecibosEventoItem,
  onDeleteAll,
  deleteAllLabel = "Borrar Todos los Egresados",
  deleteAllDisabled = false,
  onViewQR,
  customSearch,
  isEmployee,
  tieneEvento,
  snackbarOpen,
  snackbarMessage,
}: TableProps<T>) {
  const [searchText, setSearchText] = useState("")
  const [filteredData, setFilteredData] = useState<T[]>(data)
  const [isLoading, setIsLoading] = useState(false)
  const [sortColumn, setSortColumn] = useState<keyof T | null>(null)
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | null>(
    null,
  )

  useEffect(() => {
    let isMounted = true

    const handleSearch = async () => {
      if (customSearch) {
        setIsLoading(true)
        const filteredData = await customSearch(searchText)
        if (isMounted) {
          setFilteredData(filteredData)
          setIsLoading(false)
        }
      } else {
        setFilteredData(data.filter((item) => defaultSearch(item, searchText)))
      }
    }

    handleSearch()

    return () => {
      isMounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, data, customSearch])

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value)
  }

  const handleSearchSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (customSearch) {
      setIsLoading(true)
      const filteredData = await customSearch(searchText)
      setFilteredData(filteredData)
      setIsLoading(false)
    }
  }

  const defaultSearch = (item: T, search: string): boolean =>
    columns.some((column) => {
      const value = item[column.field]
      return (
        typeof value === "string" &&
        value.toLowerCase().includes(search.toLowerCase())
      )
    })

  const handleSort = (field: keyof T) => {
    if (sortColumn === field) {
      // Ciclo: asc -> desc -> null (desactivado)
      if (sortDirection === "asc") {
        setSortDirection("desc")
      } else if (sortDirection === "desc") {
        setSortDirection(null)
        setSortColumn(null)
      }
    } else {
      setSortColumn(field)
      setSortDirection("asc")
    }
  }

  const getSortedData = () => {
    if (!sortColumn || !sortDirection) return filteredData

    const column = columns.find((col) => col.field === sortColumn)

    return [...filteredData].sort((a, b) => {
      let comparison = 0

      if (column?.customSort) {
        comparison = column.customSort(a, b)
      } else {
        const aValue = a[sortColumn]
        const bValue = b[sortColumn]

        if (aValue == null && bValue == null) comparison = 0
        else if (aValue == null) comparison = -1
        else if (bValue == null) comparison = 1
        else if (typeof aValue === "string" && typeof bValue === "string") {
          comparison = aValue.localeCompare(bValue)
        } else if (typeof aValue === "number" && typeof bValue === "number") {
          comparison = aValue - bValue
        } else {
          comparison = String(aValue).localeCompare(String(bValue))
        }
      }

      return sortDirection === "asc" ? comparison : -comparison
    })
  }

  const sortedData = getSortedData()

  return (
    <Container fluid className="generic-table-container">
      <Row className="align-items-center">
        <div
          className="d-flex align-items-center w-100 flex-wrap"
          style={{ gap: "8px" }}
        >
          <div
            className="d-flex align-items-center flex-wrap"
            style={{ gap: "8px" }}
          >
            {(tieneEvento || isEmployee) && actions.create && (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="tooltip-descargar">Agregar nuevo</Tooltip>
                }
              >
                <Button variant="primary" onClick={() => onAdd?.()}>
                  <i className="bi bi-plus-square"></i>
                </Button>
              </OverlayTrigger>
            )}
            {tieneEvento && (
              <>
                {actions.create && (
                  <Button variant="primary" onClick={onImport}>
                    Importar desde Excel
                  </Button>
                )}
                {actions.export && (
                  <Button variant="success" onClick={onExport}>
                    Exportar a Excel
                  </Button>
                )}
              </>
            )}
          </div>
          {(onRecibosEvento || onDeleteAll) && (
            <div className="d-flex flex-wrap ms-auto" style={{ gap: "8px" }}>
              {onRecibosEvento && (
                <Button variant="outline-primary" onClick={onRecibosEvento}>
                  <i className="bi bi-receipt me-2"></i>
                  Ver recibos del evento
                </Button>
              )}
              {onDeleteAll && (
                <Button variant="danger" onClick={onDeleteAll} disabled={deleteAllDisabled}>
                  {deleteAllLabel}
                </Button>
              )}
            </div>
          )}
        </div>

        <Col xs={12} sm={12} className="mt-2">
          <form onSubmit={handleSearchSubmit} className="d-flex">
            <InputGroup>
              <FormControl
                placeholder={
                  isEmployee
                    ? "Buscar por Nombre o Apellido"
                    : "Buscar por Nombre, Apellido o DNI"
                }
                aria-label="Search"
                aria-describedby="basic-addon2"
                onChange={handleSearchChange}
                value={searchText}
              />
              <Button
                variant="outline-secondary"
                type="submit"
                disabled={isLoading}
              >
                <i className="bi bi-search"></i>
              </Button>
            </InputGroup>
          </form>
        </Col>
      </Row>
      <div className="generic-table-scroll table-responsive mt-3">
        <Table striped bordered hover>
          <thead>
            <tr>
              <th>Nº</th>
              {(actions.viewQR || actions.sendPublicQrWhatsapp) && (
                <th style={{ width: "125px", whiteSpace: "nowrap" }}>QR</th>
              )}
              {columns.map((column, index) => (
                <th
                  key={index}
                  style={{
                    width: `${column.width ? column.width * 80 : ""}%`,
                    ...(column.sortable && {
                      cursor: "pointer",
                      userSelect: "none",
                    }),
                  }}
                  onClick={
                    column.sortable ? () => handleSort(column.field) : undefined
                  }
                >
                  {column.title}
                  {column.sortable && (
                    <span
                      style={{
                        fontSize: "0.9em",
                        display: "inline-block",
                        width: "1.5em",
                        textAlign: "center",
                      }}
                    >
                      {sortColumn === column.field && sortDirection ? (
                        sortDirection === "asc" ? (
                          "▲"
                        ) : (
                          "▼"
                        )
                      ) : (
                        <span style={{ color: "#d3d3d3" }}>▲▼</span>
                      )}
                    </span>
                  )}
                </th>
              ))}
              {(actions.update || actions.delete || actions.view) && (
                <th style={{ width: "150px", whiteSpace: "nowrap" }}>
                  Acciones
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {sortedData.map((item, index) => (
              <tr key={index}>
                <td>{index + 1}</td>

                {(actions.viewQR || actions.sendPublicQrWhatsapp) && (
                  <td>
                    <div className="qr-speed-dial">
                      <Button
                        variant="link"
                        className="qr-action-button qr-speed-dial__trigger mx-1 my-1 px-2 py-1"
                      >
                        <i className="bi bi-three-dots-vertical qr-action-icon" />
                      </Button>
                      <div className="qr-speed-dial__actions">
                        {actions.viewQR && (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id="tooltip-descargar">Generar QR</Tooltip>
                            }
                          >
                            <Button
                              variant="primary"
                              className="qr-action-button mx-1 my-1 px-2 py-1"
                              onClick={() => onViewQR!(item)}
                            >
                              <i className="bi bi-qr-code qr-action-icon"></i>
                            </Button>
                          </OverlayTrigger>
                        )}
                        {actions.sendPublicQrWhatsapp && (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id="tooltip-link-publico-qr">
                                Enviar QR por WhatsApp
                              </Tooltip>
                            }
                          >
                            <Button
                              variant="success"
                              className="qr-whatsapp-button mx-1 my-1 px-2 py-1"
                              onClick={() => onSendPublicQrWhatsapp!(item)}
                            >
                              <span className="qr-whatsapp-icon" aria-hidden="true">
                                <i className="bi bi-qr-code qr-whatsapp-icon__qr"></i>
                                <i className="bi bi-whatsapp qr-whatsapp-icon__wa"></i>
                              </span>
                            </Button>
                          </OverlayTrigger>
                        )}
                      </div>
                    </div>
                  </td>
                )}

                {columns.map((column, key) => (
                  <td key={key}>
                    {column.render
                      ? column.render(item)
                      : item[column.field] == null
                        ? "-"
                        : String(item[column.field])}
                  </td>
                ))}
                <td style={{ width: "150px", whiteSpace: "nowrap" }}>
                  {actions.onRecibos && !showRecibosEventoItem?.(item) && (
                    <Button
                      variant="outline-primary"
                      onClick={() => onRecibos!(item)}
                      className="mx-2"
                    >
                      Ver Recibos
                    </Button>
                  )}
                  {showRecibosEventoItem?.(item) && onRecibosEventoItem && (
                    <Button
                      variant="outline-primary"
                      onClick={() => onRecibosEventoItem(item)}
                      className="mx-2"
                    >
                      Ver Recibos Evento
                    </Button>
                  )}
                  {actions.copy && (
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="tooltip-descargar">
                          Copiar contraseña
                        </Tooltip>
                      }
                    >
                      <Button
                        variant="info"
                        className="mx-2"
                        onClick={() => onCopy!(item)}
                      >
                        <i className="bi bi-clipboard"></i>
                      </Button>
                    </OverlayTrigger>
                  )}
                  {
                    <Snackbar
                      open={snackbarOpen} // Usar el booleano para abrir el Snackbar
                      anchorOrigin={{ vertical: "top", horizontal: "center" }}
                      autoHideDuration={3000}
                    >
                      <Alert severity="success">{snackbarMessage}</Alert>
                    </Snackbar>
                  }
                  {actions.update && (
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip id="tooltip-descargar">Editar</Tooltip>}
                    >
                      <Button
                        variant="primary"
                        className="mx-2"
                        onClick={() => onUpdate!(item)}
                      >
                        <i className="bi bi-pencil-square"></i>
                      </Button>
                    </OverlayTrigger>
                  )}
                  {actions.delete && (
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="tooltip-descargar">Eliminar</Tooltip>
                      }
                    >
                      <Button
                        variant="danger"
                        className="mx-2"
                        onClick={() => onDelete!(item)}
                      >
                        <i className="bi bi-trash"></i>
                      </Button>
                    </OverlayTrigger>
                  )}
                  {actions.view && (
                    <Button
                      variant="info"
                      className="mx-2"
                      onClick={() => onView!(item)}
                    >
                      <i className="bi bi-eye"></i>
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </Container>
  )
}

export default GenericTable
